/*
 * @Description: Coupon组件 - React版本（不包含阶梯券）
 * @Author: AI Assistant
 * @Date: 2024-12-19
 */

import Image from '@/components/Image';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { cva } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import React, { useCallback, useContext, useMemo } from 'react';
import NormalCoupon, { type CouponItem } from './NormalCoupon';

const couponImageVariants = cva('absolute top-0', {
  variants: {
    isWeb: {
      true: 'w-[10px] h-[80px]',
      false: 'w-[7.6px] h-[61px]',
    },
  },
  defaultVariants: {
    isWeb: true,
  },
});

const couponContainerVariants = cva('relative bg-no-repeat bg-center bg-cover', {
  variants: {
    isWeb: {
      true: '',
      false: '',
    },
    isLoggedIn: {
      true: 'login',
      false: 'unlogin',
    },
  },
  compoundVariants: [
    {
      isWeb: true,
      isLoggedIn: true,
      className: 'h-[80px]',
    },
    {
      isWeb: true,
      isLoggedIn: false,
      className: 'h-[80px]',
    },
    {
      isWeb: false,
      isLoggedIn: true,
      className: 'h-[66px]',
    },
    {
      isWeb: false,
      isLoggedIn: false,
      className: 'h-[61px]',
    },
  ],
  defaultVariants: {
    isWeb: true,
    isLoggedIn: false,
  },
});

const labelOrderTagVariants = cva(
  'inline-block absolute top-0 left-0 z-10 px-[5px] py-[2px] whitespace-nowrap',
  {
    variants: {
      isWeb: {
        true: 'text-[12px] leading-[14px] rounded-tl-[6px] rounded-br-[6px]',
        false: 'text-[10px] leading-[10px] rounded-tl-[4px] rounded-br-[4px]',
      },
    },
    defaultVariants: {
      isWeb: true,
    },
  },
);

const couponContentVariants = cva('flex flex-col items-center justify-center notranslate', {
  variants: {
    isWeb: {
      true: '',
      false: '',
    },
    isLoggedIn: {
      true: 'w-full rounded-md',
      false: '',
    },
  },
  compoundVariants: [
    {
      isWeb: true,
      isLoggedIn: true,
      className: 'h-[80px] w-full rounded-md',
    },
    {
      isWeb: true,
      isLoggedIn: false,
      className: 'h-[80px] w-[calc(100%-20px)] mx-10 rounded-none',
    },
    {
      isWeb: false,
      isLoggedIn: false,
      className: 'h-[61px] mx-[7.6px] w-[calc(100%-15.2px)]',
    },
    {
      isWeb: false,
      isLoggedIn: true,
      className: 'h-[66px] w-full rounded-md',
    },
  ],
  defaultVariants: {
    isWeb: true,
    isLoggedIn: false,
  },
});

interface CouponOldVersionProps {
  item: CouponItem;
  onCountDownFinish?: () => void;
  dividerBgColor?: string; // 分隔线背景色，传递给NormalCoupon组件
}

const CouponOldVersion: React.FC<CouponOldVersionProps> = ({
  item,
  onCountDownFinish,
  dividerBgColor,
}) => {
  const t = useTranslations();
  const token = useStore((state) => state.token);
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;

  // 优惠券类型，1折扣 2满减 3指定价 4免费
  const couponType = useMemo(() => {
    const way = Number(item.way);
    const typeMap: Record<number, number> = {
      1: 1,
      5: 1,
      2: 2,
      3: 3,
      4: 4,
    };
    return typeMap[way] || 0;
  }, [item.way]);

  // 优惠券详情对象
  const details = useMemo(() => {
    const detailsMap: Record<number, keyof CouponItem> = {
      1: 'discounts_details',
      2: 'full_discounts_details',
      3: 'appoint_discounts_details',
      4: 'free_discounts_details',
    };
    return (item[detailsMap[couponType]] as any) || {};
  }, [item, couponType]);

  // 1未领取 2已领取 3不可用
  const status = useMemo(() => {
    if (item.is_get === 1) {
      return item.code_status === 1 ? 1 : 3;
    }
    return item.is_get; // is_get == 2已领取
  }, [item.is_get, item.code_status]);

  // 优惠券标签配置
  const couponLabel = useMemo(() => {
    return item.extension_field?.coupon_label;
  }, [item.extension_field?.coupon_label]);

  // 是否显示标签：coupon_tag 为 2 或 3 时显示，1 或不存在时不显示
  const shouldShowLabel = useMemo(() => {
    return couponLabel?.coupon_tag === 2 || couponLabel?.coupon_tag === 3;
  }, [couponLabel?.coupon_tag]);

  // 标签样式
  const labelStyle = useMemo(() => {
    if (!couponLabel) return {};

    const style: React.CSSProperties = {
      color: couponLabel.text_color || '#282828',
    };

    // 根据 bg_color_type 设置背景
    if (couponLabel.bg_color_type === 2 && couponLabel.bg_start_color && couponLabel.bg_end_color) {
      // 渐变背景
      style.background = `linear-gradient(to right, ${couponLabel.bg_start_color}, ${couponLabel.bg_end_color})`;
      style.backgroundSize = '100% 100%';
    } else if (couponLabel.bg_color_type === 1 && couponLabel.bg_color) {
      // 纯色背景
      style.backgroundColor = couponLabel.bg_color;
    }

    return style;
  }, [couponLabel]);

  // 是否为阶梯券，true是 false否
  // const isLadder = useMemo(() => {
  //   return details?.discounts_type === 2;
  // }, [details?.discounts_type]);

  // 是否展示倒计时
  const isShowCountDown = useMemo(() => {
    const condition1 = status === 2; // 已领取
    const condition2 = item.count_down === 1; // 展示倒计时
    const condition3 = (item.twenty_four_count_time || 0) > 0; // 倒计时未结束
    return condition1 && condition2 && condition3;
  }, [status, item.count_down, item.twenty_four_count_time]);

  // 是否展示有效期
  const isShowValidDate = useMemo(() => {
    const condition1 = status === 2; // 已领取
    const condition2 = item.count_down === 2; // 不展示倒计时
    const condition3 = (item.twenty_four_count_time || 0) <= 0; // 倒计时已结束
    return condition1 && (condition2 || condition3);
  }, [status, item.count_down, item.twenty_four_count_time]);

  // 有效期的文字展示
  const validText = useMemo(() => {
    if (!token) return ''; // 未登录不展示有效期文字
    const timeInterval = item.time_interval || [];
    if (!isShowValidDate || !Array.isArray(timeInterval)) return '';

    if (timeInterval.length >= 1 && timeInterval[1] !== '') {
      return `${timeInterval[0]} - ${timeInterval[1]}`;
    }
    if (timeInterval.length >= 1 && timeInterval[1] === '') {
      return `Expired: ${timeInterval[0]}`;
    }
    if (timeInterval.length === 0) {
      return t('coupons.noExpiryDate');
    }
    return '';
  }, [token, item.time_interval, isShowValidDate]);

  const handleCountDownFinish = useCallback(() => {
    onCountDownFinish?.();
  }, [onCountDownFinish]);

  const savePng: Record<string, string> = {
    de: '/coupon/saved.de.png',
    fr: '/coupon/saved.fr.png',
  };

  return (
    <div className={couponContainerVariants({ isWeb, isLoggedIn: !!token })}>
      {/* 未登录时显示左右背景图 */}
      {!token && (
        <>
          <Image
            src="/coupon/coupon_left.png"
            width={10}
            height={80}
            className={couponImageVariants({ isWeb, className: 'left-0' })}
            alt="left coupon background"
          />
          <Image
            src="/coupon/coupon_left.png"
            width={10}
            height={80}
            className={couponImageVariants({ isWeb, className: 'right-0 rotate-180' })}
            alt="right coupon background"
          />
        </>
      )}

      {/* 优惠券标签 */}
      {shouldShowLabel && couponLabel?.tag_text && (
        <span className={labelOrderTagVariants({ isWeb })} style={labelStyle}>
          {couponLabel.tag_text}
        </span>
      )}

      {/* 优惠券内容 */}
      <div className={couponContentVariants({ isWeb, isLoggedIn: !!token })}>
        <NormalCoupon
          item={item}
          details={details}
          status={status}
          couponType={couponType}
          isShowCountDown={isShowCountDown}
          isShowValidDate={isShowValidDate}
          validText={validText}
          onCountDownFinish={handleCountDownFinish}
          dividerBgColor={dividerBgColor}
          isWeb={isWeb}
        />
      </div>

      {/* 已领取标识 */}
      {status === 2 && (
        <Image
          className={`absolute right-0 top-0 h-auto ${isWeb ? 'w-[48px]' : 'w-[38px]'}`}
          src={savePng[env.NEXT_PUBLIC_SITE_NAME] || '/coupon/saved.png'}
          width={48}
          height={48}
          alt="coupon saved tag"
        />
      )}
    </div>
  );
};

export default CouponOldVersion;
